import { create } from "zustand";
import type {
  SelectOptionLookup,
  CountyLookup,
  StateLookup,
  PermissionLookup,
} from "../types/flehs";

// Stable empty arrays for selectors (prevents new reference on every render)
const EMPTY_SELECT_OPTIONS: SelectOptionLookup[] = [];

// API client will be set by the hook
let apiClient: any = null;

// Simple boolean flags for race prevention (module-level, not in state)
let loadAllStarted = false;
let globalLoadStarted = false;
const moduleLoadStarted = new Set<number>();

export const setLookupApiClient = (client: any) => {
  apiClient = client;
};

// Module-specific lookups keyed by moduleId (1=Sentinel, 2=Veterinary, 3=Mosquito)
type ModuleLookups<T> = { [moduleId: number]: T[] };

// Memoized selector cache - prevents creating new function references on every render
type SelectorFn = (state: LookupStoreState) => SelectOptionLookup[];
const selectorCache: { [key: string]: SelectorFn } = {};

const getOrCreateSelector = (
  lookupType: keyof LookupStoreState,
  moduleId: number
): SelectorFn => {
  const cacheKey = `${lookupType}-${moduleId}`;
  if (!selectorCache[cacheKey]) {
    selectorCache[cacheKey] = (state: LookupStoreState) => {
      const lookupData = state[lookupType] as ModuleLookups<SelectOptionLookup>;
      return lookupData[moduleId] ?? EMPTY_SELECT_OPTIONS;
    };
  }
  return selectorCache[cacheKey];
};

// Selector helpers that return stable memoized functions
export const selectOrganizations = (moduleId: number) =>
  getOrCreateSelector("organizations", moduleId);
export const selectSpecies = (moduleId: number) =>
  getOrCreateSelector("species", moduleId);
export const selectEpiStatuses = (moduleId: number) =>
  getOrCreateSelector("epiStatuses", moduleId);
export const selectDiseaseTypes = (moduleId: number) =>
  getOrCreateSelector("diseaseTypes", moduleId);
export const selectTestingMethods = (moduleId: number) =>
  getOrCreateSelector("testingMethods", moduleId);
export const selectTestResultValues = (moduleId: number) =>
  getOrCreateSelector("testResultValues", moduleId);
export const selectTestTypes = (moduleId: number) =>
  getOrCreateSelector("testTypes", moduleId);
export const selectSpecimenTypes = (moduleId: number) =>
  getOrCreateSelector("specimenTypes", moduleId);
export const selectVaccinationStatuses = (moduleId: number) =>
  getOrCreateSelector("vaccinationStatuses", moduleId);

interface LookupStoreState {
  // Global lookups (not module-specific)
  animalStatuses: SelectOptionLookup[];
  counties: CountyLookup[];
  modules: SelectOptionLookup[];
  permissions: PermissionLookup[];
  states: StateLookup[];

  // Module-specific lookups keyed by moduleId
  diseaseTypes: ModuleLookups<SelectOptionLookup>;
  epiStatuses: ModuleLookups<SelectOptionLookup>;
  organizations: ModuleLookups<SelectOptionLookup>;
  species: ModuleLookups<SelectOptionLookup>;
  specimenTypes: ModuleLookups<SelectOptionLookup>;
  testingMethods: ModuleLookups<SelectOptionLookup>;
  testResultValues: ModuleLookups<SelectOptionLookup>;
  testTypes: ModuleLookups<SelectOptionLookup>;
  vaccinationStatuses: ModuleLookups<SelectOptionLookup>;

  // Loading states (for UI spinners only)
  loading: {
    global: boolean;
    modules: { [moduleId: number]: boolean };
  };

  // Loaded flags
  loaded: {
    global: boolean;
    modules: { [moduleId: number]: boolean };
  };

  // Progress tracking for turbo loader (0-100)
  progress: {
    current: number;
    total: number;
    isLoading: boolean;
  };

  // Actions
  loadGlobalLookups: () => Promise<void>;
  loadModuleLookups: (moduleId: number) => Promise<void>;
  loadAllModules: () => Promise<void>;
  reset: () => void;

  // Getters - these now need moduleId for module-specific lookups
  getOrganizations: (moduleId: number) => SelectOptionLookup[];
  getSpecies: (moduleId: number) => SelectOptionLookup[];
  getEpiStatuses: (moduleId: number) => SelectOptionLookup[];
  getDiseaseTypes: (moduleId: number) => SelectOptionLookup[];
  getTestingMethods: (moduleId: number) => SelectOptionLookup[];
  getTestResultValues: (moduleId: number) => SelectOptionLookup[];
  getTestTypes: (moduleId: number) => SelectOptionLookup[];
  getSpecimenTypes: (moduleId: number) => SelectOptionLookup[];
  getVaccinationStatuses: (moduleId: number) => SelectOptionLookup[];

  // Global getters
  getCountyById: (id: number) => CountyLookup | undefined;
  getCountyByName: (name: string) => CountyLookup | undefined;
  getStateById: (id: number) => StateLookup | undefined;
  getStateByAbbreviation: (abbr: string) => StateLookup | undefined;
  hasPermission: (permissionName: string) => boolean;
}

export const useLookupStore = create<LookupStoreState>((set, get) => ({
  // Initial global data
  animalStatuses: [],
  counties: [],
  modules: [],
  permissions: [],
  states: [],

  // Initial module-specific data (empty objects)
  diseaseTypes: {},
  epiStatuses: {},
  organizations: {},
  species: {},
  specimenTypes: {},
  testingMethods: {},
  testResultValues: {},
  testTypes: {},
  vaccinationStatuses: {},

  // Initial loading states (for UI spinners only)
  loading: {
    global: false,
    modules: {},
  },

  // Initial loaded flags
  loaded: {
    global: false,
    modules: {},
  },

  // Initial progress state
  progress: {
    current: 0,
    total: 0,
    isLoading: false,
  },

  // Load global (non-module-specific) lookups in order:
  // 1. Permissions (for nav filtering)
  // 2. Global lookups (counties, states, animalStatuses)
  // 3. Modules list
  loadGlobalLookups: async () => {
    // Check module-level flag FIRST (synchronous, prevents race)
    if (globalLoadStarted) {
      return;
    }

    const state = get();
    if (state.loaded.global) {
      return;
    }
    if (!apiClient) {
      return;
    }

    // Set flag IMMEDIATELY (before any await)
    globalLoadStarted = true;
    set((s) => ({ loading: { ...s.loading, global: true } }));

    // Helper to increment progress
    const incrementProgress = () => {
      set((s) => ({
        progress: { ...s.progress, current: s.progress.current + 1 },
      }));
    };

    try {
      // Step 1: Load permissions first (needed for nav)
      const permissions = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient.api("api/lookups/permission").get();
        return response.content || [];
      });
      incrementProgress();

      // Step 2: Load global lookups sequentially to avoid @fdoh/api-client race condition
      const animalStatuses = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient.api("api/lookups/animalStatus").get();
        return response.content || [];
      });
      incrementProgress();

      const counties = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient.api("api/lookups/county").get();
        return response.content || [];
      });
      incrementProgress();

      const states = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient.api("api/lookups/state").get();
        return response.content || [];
      });
      incrementProgress();

      // Step 3: Load modules list
      const modules = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient.api("api/lookups/modules").get();
        return response.content || [];
      });
      incrementProgress();

      set((s) => ({
        permissions,
        animalStatuses,
        counties,
        states,
        modules,
        loading: { ...s.loading, global: false },
        loaded: { ...s.loaded, global: true },
      }));
    } catch (error) {
      console.error("Failed to load global lookups:", error);
      set((s) => ({ loading: { ...s.loading, global: false } }));
      globalLoadStarted = false; // Reset on error to allow retry
    }
  },

  // Load lookups for a specific module
  loadModuleLookups: async (moduleId: number) => {
    // Check module-level flag FIRST (synchronous, prevents race)
    if (moduleLoadStarted.has(moduleId)) {
      return;
    }

    const state = get();
    if (state.loaded.modules[moduleId]) {
      return;
    }
    if (!apiClient) {
      return;
    }

    // Set flag IMMEDIATELY (before any await)
    moduleLoadStarted.add(moduleId);
    set((s) => ({
      loading: {
        ...s.loading,
        modules: { ...s.loading.modules, [moduleId]: true },
      },
    }));

    // Helper to increment progress
    const incrementProgress = () => {
      set((s) => ({
        progress: { ...s.progress, current: s.progress.current + 1 },
      }));
    };

    try {
      // Load all module-specific lookups SEQUENTIALLY to avoid @fdoh/api-client race condition
      // The API client has a bug where parallel requests return mixed-up responses

      const diseaseTypes = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/diseaseTypes?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const epiStatuses = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/epiStatus?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const organizations = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/organization?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const species = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/species?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const specimenTypes = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/specimentType?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const testingMethods = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/testingMethod?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const testResultValues = await apiClient.authenticatedRequest(
        async () => {
          const response = await apiClient
            .api(`api/lookups/testResultValue?moduleId=${moduleId}`)
            .get();
          return response.content || [];
        }
      );
      incrementProgress();

      const testTypes = await apiClient.authenticatedRequest(async () => {
        const response = await apiClient
          .api(`api/lookups/testType?moduleId=${moduleId}`)
          .get();
        return response.content || [];
      });
      incrementProgress();

      const vaccinationStatuses = await apiClient.authenticatedRequest(
        async () => {
          const response = await apiClient
            .api(`api/lookups/vaccinationStatus?moduleId=${moduleId}`)
            .get();
          return response.content || [];
        }
      );
      incrementProgress();

      set((s) => ({
        diseaseTypes: { ...s.diseaseTypes, [moduleId]: diseaseTypes },
        epiStatuses: { ...s.epiStatuses, [moduleId]: epiStatuses },
        organizations: { ...s.organizations, [moduleId]: organizations },
        species: { ...s.species, [moduleId]: species },
        specimenTypes: { ...s.specimenTypes, [moduleId]: specimenTypes },
        testingMethods: { ...s.testingMethods, [moduleId]: testingMethods },
        testResultValues: {
          ...s.testResultValues,
          [moduleId]: testResultValues,
        },
        testTypes: { ...s.testTypes, [moduleId]: testTypes },
        vaccinationStatuses: {
          ...s.vaccinationStatuses,
          [moduleId]: vaccinationStatuses,
        },
        loading: {
          ...s.loading,
          modules: { ...s.loading.modules, [moduleId]: false },
        },
        loaded: {
          ...s.loaded,
          modules: { ...s.loaded.modules, [moduleId]: true },
        },
      }));
    } catch (error) {
      console.error(`Failed to load lookups for module ${moduleId}:`, error);
      set((s) => ({
        loading: {
          ...s.loading,
          modules: { ...s.loading.modules, [moduleId]: false },
        },
      }));
      moduleLoadStarted.delete(moduleId); // Reset on error to allow retry
    }
  },

  // Load all modules dynamically from the modules list
  // Excludes FlehsApplication (id=4) as it's not a data module
  loadAllModules: async () => {
    // Check module-level flag FIRST (synchronous, prevents race)
    if (loadAllStarted) {
      return;
    }

    const state = get();
    // Guard: Don't reload if already fully loaded
    if (state.loaded.global && Object.keys(state.loaded.modules).length >= 3) {
      return;
    }

    // Set flag IMMEDIATELY (before any await)
    loadAllStarted = true;

    // Initialize progress: 5 global lookups + (9 lookups × 3 modules) = 32 total
    // We use 32 as estimate; actual count depends on modules returned from API
    const GLOBAL_LOOKUP_COUNT = 5;
    const MODULE_LOOKUP_COUNT = 9;
    const ESTIMATED_MODULE_COUNT = 3;
    const estimatedTotal =
      GLOBAL_LOOKUP_COUNT + MODULE_LOOKUP_COUNT * ESTIMATED_MODULE_COUNT;

    set({
      progress: { current: 0, total: estimatedTotal, isLoading: true },
    });

    try {
      await get().loadGlobalLookups();

      // Get fresh state after global lookups are loaded
      const { modules } = get();

      // Load lookups for all modules except FlehsApplication (id=4)
      // IMPORTANT: Must load sequentially due to @fdoh/api-client race condition bug
      const dataModuleIds = modules
        .filter((m) => m.id !== 4) // Exclude FlehsApplication
        .map((m) => m.id);

      // Update total with actual module count
      const actualTotal =
        GLOBAL_LOOKUP_COUNT + MODULE_LOOKUP_COUNT * dataModuleIds.length;
      set((s) => ({
        progress: { ...s.progress, total: actualTotal },
      }));

      for (const id of dataModuleIds) {
        await get().loadModuleLookups(id);
      }

      // Mark progress complete
      set((s) => ({
        progress: {
          ...s.progress,
          current: s.progress.total,
          isLoading: false,
        },
      }));
    } catch (error) {
      console.error("Failed to load all modules:", error);
      set({ progress: { current: 0, total: 0, isLoading: false } });
      loadAllStarted = false; // Reset on error to allow retry
    }
  },

  // Reset all lookups
  reset: () => {
    // Reset module-level flags
    loadAllStarted = false;
    globalLoadStarted = false;
    moduleLoadStarted.clear();

    set({
      animalStatuses: [],
      counties: [],
      modules: [],
      permissions: [],
      states: [],
      diseaseTypes: {},
      epiStatuses: {},
      organizations: {},
      species: {},
      specimenTypes: {},
      testingMethods: {},
      testResultValues: {},
      testTypes: {},
      vaccinationStatuses: {},
      loading: { global: false, modules: {} },
      loaded: { global: false, modules: {} },
      progress: { current: 0, total: 0, isLoading: false },
    });
  },

  // Module-specific getters
  getOrganizations: (moduleId: number) => get().organizations[moduleId] || [],
  getSpecies: (moduleId: number) => get().species[moduleId] || [],
  getEpiStatuses: (moduleId: number) => get().epiStatuses[moduleId] || [],
  getDiseaseTypes: (moduleId: number) => get().diseaseTypes[moduleId] || [],
  getTestingMethods: (moduleId: number) => get().testingMethods[moduleId] || [],
  getTestResultValues: (moduleId: number) =>
    get().testResultValues[moduleId] || [],
  getTestTypes: (moduleId: number) => get().testTypes[moduleId] || [],
  getSpecimenTypes: (moduleId: number) => get().specimenTypes[moduleId] || [],
  getVaccinationStatuses: (moduleId: number) =>
    get().vaccinationStatuses[moduleId] || [],

  // Global getters
  getCountyById: (id: number) =>
    get().counties.find((county) => county.id === id),
  getCountyByName: (name: string) =>
    get().counties.find(
      (county) => county.name?.toLowerCase() === name.toLowerCase()
    ),
  getStateById: (id: number) => get().states.find((state) => state.id === id),
  getStateByAbbreviation: (abbr: string) =>
    get().states.find(
      (state) => state.abbreviation?.toLowerCase() === abbr.toLowerCase()
    ),
  hasPermission: (permissionName: string) =>
    get().permissions.some((p) => p.name === permissionName && p.enabled),
}));
